// "Waarom verbonden" (Verkennen ronde 4, G3): een lijn is een lijst zinnen.
//
// Klik op een verband en je leest de herkomst in gewone taal, de redenering
// van de leeslaag of het oordeel, de zekerheid als band, en de bronnen die
// beide punten samen noemen, elk met een klik naar de alinea. Geen enkele
// grafentool doet dit (onderzoek 02-09); de data ervoor stond al in de
// database.

import { useEffect, useState } from "react";

import { BetaalmuurPil, WaaropPil, WiePil } from "./Bewijs";
import { EmptyReason } from "./Empty";
import { PanelHeader, SectionLabel } from "./ui";
import type { GraafVerbandResponse } from "../contracts";
import { api } from "../lib/api";
import { TREDE_LABEL, tweeZekerheden } from "../lib/schermen";
import { zekerheidBand } from "../lib/verkennen";
import { color, radius, size, weight } from "../tokens";

const SOORT_TEKST: Record<string, string> = {
  bron: "vraagt om", ouder_kind: "omvat", breder_smaller: "overlapt met", verwant: "is verwant aan", onderwerp: "hoort bij",
};
const UITGEVER_LABEL: Record<string, string> = {
  beleid: "beleid en toezicht", onderzoek: "onderzoek", arbeidsmarktdata: "arbeidsmarktdata",
  bedrijf: "bedrijfsleven", media: "vakmedia", onderwijs: "onderwijs", regionaal: "regionaal", gemengd: "gemengd",
};

export function VerbandPaneel({
  van,
  naar,
  onClose,
  onOpenPunt,
  onOpenAlinea,
  onVraag,
}: {
  van: string;
  naar: string;
  onClose: () => void;
  onOpenPunt: (id: string) => void;
  onOpenAlinea: (alineaId: string, citaat?: string | null) => void;
  onVraag?: (vraag: string) => void;
}) {
  const [data, setData] = useState<GraafVerbandResponse | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    let levend = true;
    setData(null); setFout(null);
    api.graafVerband(van, naar)
      .then((d) => levend && setData(d))
      .catch((e) => levend && setFout(String(e)));
    return () => { levend = false; };
  }, [van, naar]);

  const knop = {
    cursor: "pointer", fontSize: size.label, padding: "4px 10px", borderRadius: radius.input,
    border: `1px solid ${color.border}`, background: color.surface, color: color.body, fontFamily: "inherit",
  } as const;
  const band = (tekst: string) => (
    <span style={{ fontSize: size.label, padding: "1px 8px", borderRadius: radius.pill, border: `1px solid ${color.border}`, color: color.body }}>
      {tekst}
    </span>
  );

  return (
    <div style={{ height: "100%", overflow: "auto" }}>
      <PanelHeader onClose={onClose}>
        <span style={{ fontSize: size.label, color: color.faint, letterSpacing: "0.08em", textTransform: "uppercase" }}>verband</span>
      </PanelHeader>
      <div style={{ padding: "18px 24px 24px" }}>
        {fout && <EmptyReason reason={fout.includes("404") ? "Tussen deze twee punten staat geen verband in de data." : `verband niet te laden: ${fout}`} />}
        {!data && !fout && <p style={{ color: color.faint, fontSize: size.meta }}>verband laden…</p>}
        {data && (
          <>
            <h2 style={{ fontSize: size.panelTitle, fontWeight: weight.bold, color: color.ink, lineHeight: 1.3, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
              <button type="button" onClick={() => onOpenPunt(data.van.id)} style={{ all: "unset", cursor: "pointer", borderBottom: `1px solid ${color.border}` }}>{data.van.naam}</button>
              <span style={{ color: color.faint, fontWeight: weight.regular }}> {SOORT_TEKST[data.soort] ?? data.soort} </span>
              <button type="button" onClick={() => onOpenPunt(data.naar.id)} style={{ all: "unset", cursor: "pointer", borderBottom: `1px solid ${color.border}` }}>{data.naar.naam}</button>
            </h2>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0 16px" }}>
              {band(data.trede === "bron_zegt_zelf" || data.trede === "zelfde_bron_afgeleid" ? TREDE_LABEL[data.trede] : data.trede === "zelfde_onderwerp_andere_bron" ? "afgeleid" : data.soort === "ouder_kind" ? "hiërarchie" : "AI-oordeel")}
              {band(`sterkte ${data.sterkte}${data.soort === "bron" ? (data.sterkte === 1 ? " bron" : " bronnen") : data.soort === "ouder_kind" ? "" : " stemmen"}`)}
              {/* K1 (bouwrun R1): twee getallen zodra de API ze levert; anders het oude ene. */}
              {band(tweeZekerheden(data) ?? `zekerheid ${zekerheidBand(data.zekerheid)}${data.zekerheid != null ? ` (${data.zekerheid.toFixed(2)})` : ""}`)}
            </div>
            <SectionLabel>Herkomst</SectionLabel>
            <p style={{ fontSize: size.body, color: color.body, lineHeight: 1.6, margin: "0 0 14px" }}>{data.herkomst}</p>
            {data.redenering && (
              <>
                <SectionLabel>Redenering</SectionLabel>
                <p style={{ fontSize: size.body, color: color.body, lineHeight: 1.6, margin: "0 0 14px" }}>{data.redenering}</p>
              </>
            )}
            {data.soort === "bron" && (
              <>
                <SectionLabel>De bronnen die dit dragen</SectionLabel>
                {data.bronnen.length === 0 && (
                  <p style={{ fontSize: size.meta, color: color.faint, margin: "0 0 14px" }}>
                    Geen document noemt beide in één adem; de koppeling steunt op de redenering en op de aparte bronnen van trend en vaardigheid.
                  </p>
                )}
                {data.bronnen.map((b) => (
                  <div key={b.documentId} style={{ background: color.surfaceQuote, border: `1px solid ${color.lineFaint}`, borderRadius: radius.quote, padding: "10px 12px", marginBottom: 8 }}>
                    <div style={{ fontSize: size.meta, fontWeight: weight.medium, color: color.ink }}>{b.titel ?? b.uitgever ?? "bron"}</div>
                    <div style={{ fontSize: size.label, color: color.faint, margin: "2px 0 6px" }}>
                      {[b.uitgever, b.uitgeverType ? UITGEVER_LABEL[b.uitgeverType] ?? b.uitgeverType : null, b.datum].filter(Boolean).join(" · ")}
                    </div>
                    {/* Methode v3: wie het zegt en waarop. Zonder de velden niets. */}
                    {(b.wie || b.onderbouwingLabel || b.onderbouwing || b.achterBetaalmuur) && (
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", margin: "0 0 6px" }}>
                        <WiePil bron={b} />
                        <WaaropPil bron={b} />
                        <BetaalmuurPil bron={b} />
                      </div>
                    )}
                    {b.citaatTrend && <p style={{ fontSize: size.meta, color: color.muted, margin: "0 0 4px", lineHeight: 1.5 }}><i style={{ color: color.faint, fontStyle: "normal" }}>over de trend: </i>“{b.citaatTrend}”</p>}
                    {b.citaatVaardigheid && b.citaatVaardigheid !== b.citaatTrend && <p style={{ fontSize: size.meta, color: color.muted, margin: "0 0 4px", lineHeight: 1.5 }}><i style={{ color: color.faint, fontStyle: "normal" }}>over de vaardigheid: </i>“{b.citaatVaardigheid}”</p>}
                    {b.alineaId
                      ? <button type="button" style={{ ...knop, marginTop: 4 }} onClick={() => onOpenAlinea(b.alineaId!, b.citaatTrend ?? b.citaatVaardigheid)}>Lees de alinea →</button>
                      : <span style={{ fontSize: size.label, color: color.faint }}>alinea niet gevonden in de opgeslagen tekst</span>}
                  </div>
                ))}
              </>
            )}
            {onVraag && (
              <div style={{ marginTop: 16 }}>
                <button type="button" style={knop} onClick={() => onVraag(`Waarom hangt “${data.van.naam}” samen met “${data.naar.naam}”, en hoe hard is dat bewijs?`)}>
                  Vraag de chat hierover
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
